import { useState } from 'react';
import { Shield, ShieldCheck, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact streak-freeze button + popover.
 * @param {{available:number, max_per_7d:number, missed_last_7d:string[], frozen_dates:string[]}} freeze
 * @param {'workout'|'mood'} streakType
 * @param {() => void} onApplied — called after a freeze is consumed (parent reloads)
 * @param {string} testid
 */
export function StreakFreezeButton({ freeze, streakType, onApplied, testid }) {
    const { language } = useLanguage();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const t = (de, en) => (language === 'de' ? de : en);
    const locale = language === 'de' ? deLocale : enUS;

    if (!freeze) return null;

    const available = freeze.available || 0;
    const missed = freeze.missed_last_7d || [];

    const handleFreeze = async (e, date) => {
        e.stopPropagation();
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            await axios.post(
                `${API}/api/dashboard/streak-freeze?streak_type=${streakType}&target_date=${date}`,
                null);
            toast.success(t('🛡️ Streak gerettet!', '🛡️ Streak saved!'));
            setOpen(false);
            onApplied?.();
        } catch (err) {
            console.error('Freeze error:', err);
            const msg = err?.response?.data?.detail || (language === 'de' ? 'Fehler' : 'Error');
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative inline-flex">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(!open); }}
                className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-md px-1.5 py-0.5 border transition-colors ${
                    available > 0
                        ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-500 hover:bg-cyan-500/20'
                        : 'border-border bg-muted text-muted-foreground'
                }`}
                title={t(`Streak-Schutz: ${available}/${freeze.max_per_7d} verfügbar`, `Streak shield: ${available}/${freeze.max_per_7d} available`)}
                data-testid={testid || 'streak-freeze-btn'}
            >
                {available > 0 ? <Shield className="w-2.5 h-2.5" /> : <ShieldCheck className="w-2.5 h-2.5" />}
                <span>{available}</span>
            </button>

            <AnimatePresence>
                {open && (
                    <>
                        <div
                            className="fixed inset-0 z-40"
                            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: -4, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -4, scale: 0.95 }}
                            className="absolute z-50 top-full left-0 mt-2 w-64 bg-card border-2 border-border rounded-xl shadow-2xl p-3"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`${testid || 'streak-freeze'}-popover`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-1.5">
                                    <Shield className="w-4 h-4 text-cyan-500" />
                                    <span className="text-sm font-semibold">{t('Streak-Schutz', 'Streak Shield')}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
                                {t(
                                    `${available} von ${freeze.max_per_7d} Schutz/Woche verfügbar. Schütze einen verpassten Tag, damit dein Streak nicht bricht.`,
                                    `${available} of ${freeze.max_per_7d} shield(s) available this week. Save a missed day to keep your streak alive.`
                                )}
                            </p>

                            {available === 0 ? (
                                <div className="text-center text-[11px] text-muted-foreground py-3 bg-muted/30 rounded-lg">
                                    {t('Diese Woche kein Schutz mehr übrig. Nächste Woche wieder!', 'No shields left this week. Reset next week!')}
                                </div>
                            ) : missed.length === 0 ? (
                                <div className="text-center text-[11px] text-emerald-500 py-3 bg-emerald-500/10 rounded-lg" data-testid={`${testid || 'streak-freeze'}-no-missed`}>
                                    {t('🎉 Keine verpassten Tage in letzter Woche!', '🎉 No missed days last week!')}
                                </div>
                            ) : (
                                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-1">
                                        {t('Verpasste Tage', 'Missed days')}
                                    </p>
                                    {missed.map((d) => {
                                        let dateStr = d;
                                        try {
                                            dateStr = format(parseISO(d), 'EEEE, d. MMM', { locale });
                                        } catch (e) { /* ignore */ }
                                        return (
                                            <button
                                                key={d}
                                                type="button"
                                                onClick={(e) => handleFreeze(e, d)}
                                                disabled={busy}
                                                className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-muted hover:bg-cyan-500/10 hover:border-cyan-500/30 border border-border transition-colors text-left text-xs"
                                                data-testid={`${testid || 'streak-freeze'}-day-${d}`}
                                            >
                                                <span className="truncate font-medium">{dateStr}</span>
                                                {busy ? (
                                                    <Loader2 className="w-3 h-3 animate-spin text-cyan-500" />
                                                ) : (
                                                    <Shield className="w-3 h-3 text-cyan-500" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {freeze.frozen_dates && freeze.frozen_dates.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-border">
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-1 mb-1">
                                        {t('Gespeichert', 'Recently saved')}
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {freeze.frozen_dates.slice(-4).reverse().map((d) => {
                                            let s = d;
                                            try { s = format(parseISO(d), 'd. MMM', { locale }); } catch (e) { /* ignore */ }
                                            return (
                                                <span key={d} className="inline-flex items-center gap-0.5 text-[10px] bg-cyan-500/10 text-cyan-500 px-1.5 py-0.5 rounded">
                                                    <ShieldCheck className="w-2.5 h-2.5" /> {s}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
